import {
  copyTemplate,
  copyTemplateToProject,
  deleteTemplate,
  deleteTemplates,
  editTemplateName,
  exportTemplateHTML,
  getMessageTypesAndSenders,
  getTemplatePreviewThumbnails,
} from 'api/TemplateIndexAPI';
import { createBeeTemplate, createTemplate } from 'api/TemplatePickerAPI';
import { selectUserProject } from 'core/userContext';
import { pluralizeWord } from 'modules/campaigns/lib/utils';
import { displayContent } from 'modules/templateEditor/common';
import { addNegativeToast, addPositiveToast, addWarningToast } from 'modules/toastContainer';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { addToRootSaga } from 'store';

import {
  copyTemplateResponse,
  createTemplateResponse,
  deleteTemplatesResponse,
  exportTemplateHTMLResponse,
  getMessageTypesAndSendersError,
  getMessageTypesAndSendersResponse,
  getTemplatePreviewThumbnailsResponse,
  isTemplateAsyncProcessing,
} from './actions';
import * as actionTypes from './actionTypes';
import { selectMessageTypesAndSenders } from './selectors';

// Eventually can be removed when locales improvement FF is no longer needed
const localesBetaError =
  'This project is in the Locales beta, you can only clone templates from this project to other projects also in the beta.';

// gets message types and senders for a particular project and stores it into redux
export function* getMessageTypesAndSendersSaga({ projectId, messageMedium }) {
  try {
    const { messageTypes, emailSenders, smsSenders, whatsAppSenders } = yield call(
      getMessageTypesAndSenders,
      projectId,
      messageMedium,
    );

    if (!messageTypes || !emailSenders || !smsSenders || !whatsAppSenders)
      throw new Error('MessageTypes or Senders came back undefined');

    yield put(
      getMessageTypesAndSendersResponse({
        messageTypes,
        emailSenders,
        smsSenders,
        whatsAppSenders,
        projectId,
        messageMedium,
      }),
    );
  } catch (error) {
    if (error.response?.data?.code === 'error.projects.missingMessageTypes')
      yield put(
        addNegativeToast(
          `Unable to create template. Please create a message type for this project and try again.`,
        ),
      );
    else
      yield put(
        addNegativeToast(
          `Unable to create template. Please create a verified sender for this project and try again.`,
        ),
      );
    yield put(getMessageTypesAndSendersError());
  }
}

export function* createTemplateModalSaga({
  currentFolderId,
  dragDropSlug,
  messageMedium,
  name,
  defaultLocale,
  templateType,
  placementId,
}) {
  try {
    yield put(isTemplateAsyncProcessing(true));
    yield put(addPositiveToast('Creating new template. Just a moment...'));

    const { id: projectId } = yield select(selectUserProject);
    yield getMessageTypesAndSendersSaga({ projectId, messageMedium });
    const messageTypesAndSenders = yield select(selectMessageTypesAndSenders, {
      projectId,
      messageMedium,
    });

    if (!messageTypesAndSenders) throw new Error('MessageTypes or Senders came back undefined');

    const { data } = dragDropSlug
      ? yield call(createBeeTemplate, null, messageMedium, dragDropSlug, currentFolderId)
      : yield call(
          createTemplate,
          null,
          messageMedium,
          currentFolderId,
          defaultLocale,
          templateType,
          placementId,
        );

    // creating a template returns the editTemplate page.
    // For the moment, this is necessary because the new flow isn't globally enabled.
    // Once it is globally enabled, a new endpoint can just return the templateId created.
    // but for now we need to take the templateId like so.

    const templateId = data?.globals?.__templateId__;

    yield put(createTemplateResponse(templateId, messageMedium));
    yield call(editTemplateName, templateId, name);
    // hacky way to access the window and load an angular page
    // from here. This will go away when templates are reactified.

    yield window &&
      window.location.assign(
        `/templates/edit?templateId=${templateId}${
          defaultLocale ? `&locale=${defaultLocale}` : ''
        }`,
      );
    yield put(isTemplateAsyncProcessing(false));
  } catch (error) {
    yield put(isTemplateAsyncProcessing(false));
    const { id: projectId } = yield select(selectUserProject);
    // this will return undefined if the call for senders failed
    const data = yield select(selectMessageTypesAndSenders, {
      projectId,
      messageMedium,
    });
    // only show new error toast if get message types was not the area where it failed
    if (data) {
      yield put(addNegativeToast('Unable to create template'));
    }
  }
}

export function* copyTemplateModalSaga({
  currentFolderId,
  currentCampaignId,
  destProjectId,
  destProjectName,
  name,
  refetch,
  srcTemplateId,
  triggeredBy,
  type,
  isCurrentProjInLocaleBeta, // Need to remove this once we are out of locale beta
  ...props
}) {
  try {
    yield put(addPositiveToast('Copying template. Just a moment...'));
    const data = destProjectId
      ? yield call(copyTemplateToProject, {
          srcTemplateId,
          destProjectId,
          ...props,
        })
      : yield call(
          copyTemplate,
          srcTemplateId,
          currentFolderId,
          props.defaultLocaleToClone,
          props.secondaryLocalesToClone,
        );
    // creating a template returns the editTemplate page.
    // For the moment, this is necessary because the new flow isn't globally enabled.
    // Once it is globally enabled, a new endpoint can just return the templateId created.
    // but for now we need to take the templateId like so.

    const newTemplateId = destProjectId ? data?.newTemplateId : data?.globals?.__templateId__;
    yield put(
      copyTemplateResponse({
        srcTemplateId,
        destTemplateId: newTemplateId,
        destProjectId,
        triggeredBy,
      }),
    );

    let toastMessage = `Your template was successfully copied to ${destProjectName}.`;
    let options = {
      actions: [
        {
          title: 'Click here to view it.',
          // note: when every member is using the new editing experience, change this to '/templates/editor', until then, the BE will redirect us depending on FF
          href: `/templates/editor?templateId=${newTemplateId}&projectId=${destProjectId}`,
          externalAction: true,
        },
      ],
    };

    // Can only edit templates in the current project context
    if (!destProjectId) {
      toastMessage = `Your template was successfully copied.`;
      options = {
        actions: [
          {
            title: 'Click here to view it.',
            // note: when every member is using the new editing experience, change this to '/templates/editor', until then, the BE will redirect us depending on FF
            href: `/templates/editor?templateId=${newTemplateId}`,
            externalAction: true,
          },
        ],
      };
      yield call(editTemplateName, newTemplateId, name);
      refetch();
    }

    yield put(addPositiveToast(toastMessage, options));
  } catch (error) {
    // Need to update this error messages once we are out of locale beta
    const isErrCopyFromLocaleBetaProjToNonBeta =
      error.response?.data?.message === localesBetaError && isCurrentProjInLocaleBeta;
    const isErrCopyFromNonBetaToBetaProj =
      error.response?.data?.message === localesBetaError && !isCurrentProjInLocaleBeta;

    if (isErrCopyFromLocaleBetaProjToNonBeta) {
      yield put(addNegativeToast(displayContent.localesBetaErrorForNonBetaProj));
    } else if (isErrCopyFromNonBetaToBetaProj) {
      yield put(addNegativeToast(displayContent.localesBetaErrorMessage));
    } else {
      yield put(addNegativeToast('Unable to copy template'));
    }
  }
}

export function* deleteTemplatesSaga({ templateIds, refetch, triggeredBy }) {
  try {
    yield put(addWarningToast('Starting template delete. Just a moment...'));
    if (templateIds.length === 1) yield call(deleteTemplate, templateIds[0]);
    else yield call(deleteTemplates, templateIds);

    yield put(
      addPositiveToast(
        templateIds === 1 ? 'Successfully deleted template' : 'Successfully deleted templates',
      ),
    );
    yield put(deleteTemplatesResponse(templateIds, triggeredBy));
    refetch();
  } catch (error) {
    yield put(addNegativeToast('Error deleting templates'));
  }
}

export function* getTemplatePreviewThumbnailsSaga({ templateIds }) {
  try {
    const result = yield call(getTemplatePreviewThumbnails, templateIds);
    yield put(getTemplatePreviewThumbnailsResponse(result));
  } catch (error) {
    yield put(
      addNegativeToast(`Error fetching template ${pluralizeWord('preview', templateIds?.length)}`),
    );
  }
}

export function* exportTemplateHTMLSaga({ templateId, projectName, triggeredBy }) {
  try {
    // This returns a string
    const fileText = yield call(exportTemplateHTML, templateId);

    yield put(exportTemplateHTMLResponse(templateId, triggeredBy));

    // This block creates a link that will download a file
    const element = document.createElement('a');
    // Create Fille from returned text
    const file = new Blob([fileText], { type: 'text/html' });
    element.href = URL.createObjectURL(file);
    // Set name
    element.download = `iterable_${projectName}_template_id_${templateId}.html`;
    document.body.appendChild(element); // Required for this to work in FireFox
    // downloads file
    element.click();
    document.body.removeChild(element);
  } catch (error) {
    yield put(addNegativeToast('Error exporting template'));
  }
}

addToRootSaga([
  takeLatest(actionTypes.CREATE_TEMPLATE_REQUEST, createTemplateModalSaga),
  takeLatest(actionTypes.COPY_TEMPLATE_REQUEST, copyTemplateModalSaga),
  takeLatest(actionTypes.DELETE_TEMPLATES_REQUEST, deleteTemplatesSaga),
  takeLatest(actionTypes.GET_MESSAGETYPES_AND_SENDERS_REQUEST, getMessageTypesAndSendersSaga),
  takeEvery(actionTypes.GET_TEMPLATE_PREVIEW_THUMBNAILS_REQUEST, getTemplatePreviewThumbnailsSaga),
  takeLatest(actionTypes.EXPORT_TEMPLATE_HTML_REQUEST, exportTemplateHTMLSaga),
]);
